import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Pencil } from "lucide-react";
import { notifyClassesChanged } from "@/lib/activeClass";
import { toast } from "sonner";

export default function Classes() {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [maitres, setMaitres] = useState([]);
  const [schools, setSchools] = useState([]);
  const [open, setOpen] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [form, setForm] = useState({ school_id: "", nom: "", maitre_id: "" });

  const load = async () => {
    try {
      const [c, s] = await Promise.all([api.get("/classes"), api.get("/schools")]);
      setClasses(c.data); setSchools(s.data);
      if (user.role === "admin") {
        const { data } = await api.get("/users");
        setMaitres(data.filter((u) => u.role === "maitre"));
      } else if (user.role === "directeur" || user.role === "maitre") {
        // best effort — directeur peut voir ses maitres en demandant /users? Admin only. So leave empty or use partial.
        try {
          const { data } = await api.get("/users");
          setMaitres(data.filter((u) => u.role === "maitre"));
        } catch { /* not authorized */ }
      }
    } catch (e) { toast.error(formatError(e)); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!form.school_id && schools.length > 0) {
      setForm((f) => ({ ...f, school_id: user.school_id || schools[0].id }));
    }
  }, [schools, user]);

  const create = async () => {
    try {
      await api.post("/classes", { ...form, maitre_id: form.maitre_id || null });
      toast.success("Classe créée");
      setOpen(false);
      notifyClassesChanged();
      setForm({ school_id: user.school_id || schools[0]?.id || "", nom: "", maitre_id: "" });
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const rename = async () => {
    try {
      await api.patch(`/classes/${renaming.id}`, { nom: renaming.nom });
      toast.success("Classe renommée");
      setRenaming(null);
      notifyClassesChanged();
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const del = async (c) => {
    if (!confirm(`Supprimer la classe ${c.nom} ?`)) return;
    try {
      await api.delete(`/classes/${c.id}`);
      toast.success("Supprimée");
      notifyClassesChanged();
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const canEdit = user.role === "admin" || user.role === "directeur";
  const canDelete = user.role === "admin" || user.role === "directeur";

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "Outfit" }}>Classes</h1>
          <p className="text-sm text-[#5C736A]">{classes.length} classe(s)</p>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#1F7A63] hover:bg-[#165c4b] text-white" data-testid="add-class-btn">
                <Plus className="w-4 h-4 mr-2" /> Nouvelle classe
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Créer une classe</DialogTitle></DialogHeader>
              <div className="space-y-3">
                {user.role === "admin" && (
                  <div>
                    <Label>École</Label>
                    <Select value={form.school_id} onValueChange={(v) => setForm({ ...form, school_id: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.nom}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>Nom de la classe</Label>
                  <Input data-testid="class-nom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="ex : CI A, CI B, CM2 Flux 1" maxLength={60} />
                  <p className="text-xs text-[#5C736A] mt-1">Saisissez le nom exact utilisé dans votre école.</p>
                </div>
                {user.role !== "maitre" && <div>
                  <Label>Maître (optionnel)</Label>
                  <Select value={form.maitre_id} onValueChange={(v) => setForm({ ...form, maitre_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Aucun" /></SelectTrigger>
                    <SelectContent>
                      {maitres.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button onClick={create} disabled={!form.nom.trim()} data-testid="class-submit" className="bg-[#1F7A63] hover:bg-[#165c4b] text-white">Créer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="ek-card p-0">
        <div className="ek-table-wrap">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Niveau</TableHead>
                <TableHead>Maître</TableHead>
                {canEdit && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {classes.map((c) => {
                const m = maitres.find((mm) => mm.id === c.maitre_id);
                return (
                  <TableRow key={c.id} data-testid={`class-row-${c.nom}`}>
                    <TableCell className="font-medium">{c.nom}</TableCell>
                    <TableCell>{c.niveau || "—"}</TableCell>
                    <TableCell>{m ? m.name : <span className="text-[#5C736A]">—</span>}</TableCell>
                    {canEdit && (
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => setRenaming({ id: c.id, nom: c.nom })} data-testid={`class-rename-${c.id}`} title="Renommer">
                          <Pencil className="w-4 h-4 text-[#1F7A63]" />
                        </Button>
                        {canDelete && (
                          <Button variant="ghost" size="icon" onClick={() => del(c)} data-testid={`class-delete-${c.id}`} title="Supprimer">
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              {classes.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-sm text-[#5C736A] py-8">Aucune classe. Créez-en une !</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={!!renaming} onOpenChange={(o) => !o && setRenaming(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Renommer la classe</DialogTitle></DialogHeader>
          <div>
            <Label>Nom de la classe</Label>
            <Input data-testid="class-rename-input" value={renaming?.nom || ""} maxLength={60}
              onChange={(e) => setRenaming({ ...renaming, nom: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenaming(null)}>Annuler</Button>
            <Button onClick={rename} disabled={!renaming?.nom?.trim()} data-testid="class-rename-submit" className="bg-[#1F7A63] hover:bg-[#165c4b] text-white">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
